// 表單製作器 API（Sprint 5 · Form Builder frontend PR-2）· axios instance 自動帶 auth + txn header
import axiosInstance from '../../../../api/axios';

const B = '/api/ee/forms';

export type FieldType = 'text' | 'textarea' | 'number' | 'date' | 'datetime'
  | 'select' | 'radio' | 'checkbox' | 'file' | 'hidden';
export type Capability = 'create' | 'submit_review' | 'review' | 'publish_close'
  | 'fill' | 'edit_own' | 'view_all' | 'delete';
export type FormStatus = 'draft' | 'pending_review' | 'approved' | 'published' | 'closed' | 'deleted';

export interface OptionsSource {
  type: 'static' | 'table' | 'query';
  values?: string[];
  table?: string; display?: string; value?: string;
  sql?: string;
}

export interface FieldSpec {
  id?: number;
  order: number;
  label: Record<string, string>;
  field_name: string;
  field_type: FieldType;
  field_kind?: 'data' | 'computed';
  length?: number | null;
  required: boolean;
  widget_type?: string | null;
  options_source?: OptionsSource | null;
  options?: { value: unknown; label: string }[];   // GET 時後端解析回填
}

export interface FormSummary {
  id: number; title: string; description?: string; status: FormStatus;
  owner_user_id: number; target_user_ids: number[]; rejection_reason?: string | null;
  published_at?: string | null; created_at?: string | null;
}

export interface FormDetail extends FormSummary {
  fields: FieldSpec[];
  my_submission?: { id: number; data: Record<string, unknown> } | null;
}

export interface Grant { id: number; principal_type: 'role' | 'user'; principal_id: number; capability: Capability; }
export interface Submission { id: number; user_id?: number; data: Record<string, unknown>; submitted_at?: string; }
export interface AuditEntry { id: number; action: string; actor_user_id: number; note?: string | null; created_at?: string; }

// ── manager ───────────────────────────────────────────────────────────────
export const listForms = (): Promise<{ items: FormSummary[]; total: number }> =>
  axiosInstance.get(`${B}/forms`).then((r) => r.data);
export const createForm = (body: Partial<FormDetail>): Promise<FormDetail> =>
  axiosInstance.post(`${B}/forms`, body).then((r) => r.data);
export const getForm = (id: number): Promise<FormDetail> =>
  axiosInstance.get(`${B}/forms/${id}`).then((r) => r.data);
export const updateForm = (id: number, body: Partial<FormDetail>): Promise<FormDetail> =>
  axiosInstance.put(`${B}/forms/${id}`, body).then((r) => r.data);
export const submitReview = (id: number): Promise<FormDetail> =>
  axiosInstance.post(`${B}/forms/${id}/submit-review`).then((r) => r.data);
export const reviewForm = (id: number, action: 'approve' | 'reject', note?: string): Promise<FormDetail> =>
  axiosInstance.post(`${B}/forms/${id}/review`, { action, note }).then((r) => r.data);
export const publishForm = (id: number, target_user_ids?: number[]): Promise<FormDetail> =>
  axiosInstance.post(`${B}/forms/${id}/publish`, { target_user_ids }).then((r) => r.data);
export const closeForm = (id: number): Promise<FormDetail> =>
  axiosInstance.post(`${B}/forms/${id}/close`).then((r) => r.data);
export const deleteForm = (id: number): Promise<void> =>
  axiosInstance.delete(`${B}/forms/${id}`).then(() => undefined);
export const getAuditLog = (id: number): Promise<{ items: AuditEntry[] }> =>
  axiosInstance.get(`${B}/forms/${id}/audit-log`).then((r) => r.data);

// ── grants ─────────────────────────────────────────────────────────────────
export const listGrants = (id: number): Promise<{ items: Grant[] }> =>
  axiosInstance.get(`${B}/forms/${id}/grants`).then((r) => r.data);
export const addGrant = (id: number, g: Omit<Grant, 'id'>): Promise<Grant> =>
  axiosInstance.post(`${B}/forms/${id}/grants`, g).then((r) => r.data);
export const removeGrant = (id: number, grantId: number): Promise<void> =>
  axiosInstance.delete(`${B}/forms/${id}/grants/${grantId}`).then(() => undefined);

// ── workspace ────────────────────────────────────────────────────────────────
export const listFillable = (): Promise<{ items: FormSummary[]; total: number }> =>
  axiosInstance.get(`${B}/workspace/forms`).then((r) => r.data);
export const getWorkspaceForm = (id: number): Promise<FormDetail> =>
  axiosInstance.get(`${B}/workspace/forms/${id}`).then((r) => r.data);
export const createSubmission = (id: number, data: Record<string, unknown>): Promise<Submission> =>
  axiosInstance.post(`${B}/forms/${id}/submissions`, { data }).then((r) => r.data);
export const updateSubmission = (id: number, subId: number, data: Record<string, unknown>): Promise<Submission> =>
  axiosInstance.put(`${B}/forms/${id}/submissions/${subId}`, { data }).then((r) => r.data);
export const listSubmissions = (id: number): Promise<{ items: Submission[] }> =>
  axiosInstance.get(`${B}/forms/${id}/submissions`).then((r) => r.data);

// ── print（authed blob → new tab；raw window.open 無法帶 auth header）──────────
export const openPrint = async (id: number): Promise<void> => {
  const r = await axiosInstance.get(`${B}/forms/${id}/print`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(r.data);
  const w = window.open(url, '_blank', 'noopener');
  if (w) setTimeout(() => w.print(), 400);
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};
